import Client from "..";
import { LevelType, OpponentLevel, BossLevel } from "../utils/TimelineTypes";
import { GameEvents } from "../utils/Types";
import { Global } from "../utils/Global";
import { NineSlicePresets } from "./NineSlicePresets";
import { Shake } from "../utils/Shake";

export class LevelInfoDisplay extends Phaser.GameObjects.Container {
  private background!: Phaser.GameObjects.NineSlice;
  private levelIcon!: Phaser.GameObjects.Image;
  private levelNameText!: Phaser.GameObjects.BitmapText;
  private requiredScoreText!: Phaser.GameObjects.BitmapText;

  private lastMaxWidth?: number;
  private lastMaxHeight?: number;

  constructor(scene: Phaser.Scene) {
    super(scene);

    this.createContent();
    this.updateDisplay();

    // Listen for level changes
    Client.game.events.on(GameEvents.LEVEL_UPDATED, this.onLevelUpdated, this);
    Client.game.events.on(
      GameEvents.LEVEL_ACTION_TRIGGERED,
      this.onLevelActionTriggered,
      this,
    );
  }

  private onLevelActionTriggered(): void {
    const triggerText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "Triggered!!!",
      Math.max(Global.minFontSize, 32),
    ).setTint(0xff0000);
    triggerText.setOrigin(0.5, 0.5);
    this.scene.add.existing(triggerText);

    const worldPoint = this.getWorldPoint();

    triggerText.setPosition(
      worldPoint.x + this.background.displayWidth * 0.5,
      worldPoint.y + this.background.displayHeight * 2,
    );
    this.scene.tweens.add({
      targets: triggerText,
      y: triggerText.y - this.background.displayHeight * 0.5,
      alpha: 0,
      duration: 750,
      ease: "Power2",
      onComplete: () => {
        triggerText.destroy();
      },
    });

    this.getData("shakeTween")?.complete();
    const tween = Shake(this.scene, this, {
      duration: 250,
      magnitude: 8,
      rotate: true,
      restore: true,
    });
    this.setData("shakeTween", tween);
  }

  private createContent(): void {
    this.background = NineSlicePresets.Default(this.scene).setTint(0x9c7a4d);
    this.add(this.background);

    // Create level icon (bigger than timeline icons)
    this.levelIcon = new Phaser.GameObjects.Image(
      this.scene,
      0,
      0,
      "icon_opponent",
    );
    this.levelIcon.setOrigin(0, 0);
    this.add(this.levelIcon);

    // Create level name text (bigger)
    this.levelNameText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "Level Name",
      Global.minFontSize * 1.3, // Bigger text
    );
    this.levelNameText.setOrigin(0, 0);
    this.add(this.levelNameText);

    // Create required score text (only visible for opponent levels)
    this.requiredScoreText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "Required Score: 0",
      Global.minFontSize * 1.1, // Slightly bigger text
    );
    this.requiredScoreText.setOrigin(0, 0);
    this.requiredScoreText.setVisible(false);
    this.add(this.requiredScoreText);
  }

  private onLevelUpdated(): void {
    this.updateDisplay();
  }

  private updateDisplay(): void {
    const currentLevel = Client.state.currentLevelData;

    if (!currentLevel) {
      this.setVisible(false);
      return;
    }

    this.setVisible(true);

    // Update icon - use bigIcon if available, otherwise fall back to regular icon
    const iconTexture =
      currentLevel.bigIcon || currentLevel.icon || "icon_opponent";
    this.levelIcon.setTexture(iconTexture);

    // Update level name
    this.levelNameText.setText(currentLevel.name || "Unnamed Level");

    // Update required score text for opponent levels
    if (
      currentLevel.levelType === LevelType.OPPONENT ||
      currentLevel.levelType === LevelType.BOSS ||
      currentLevel.levelType === LevelType.TUTORIAL_OPPONENT
    ) {
      const opponentLevel = currentLevel as OpponentLevel | BossLevel;
      if (opponentLevel.requiredScore !== undefined) {
        this.requiredScoreText.setText(
          `Required Score: ${opponentLevel.requiredScore}`,
        );
        this.requiredScoreText.setVisible(true);
      } else {
        this.requiredScoreText.setVisible(false);
      }
    } else {
      this.requiredScoreText.setVisible(false);
    }

    // Trigger a resize to recalculate background size for new content
    if (this.lastMaxWidth && this.lastMaxHeight) {
      this.Resize(this.lastMaxWidth, this.lastMaxHeight);
    } else {
      this.repositionElements();
    }
  }

  private repositionElements(): void {
    // Only reposition if we have a properly sized background
    if (this.background && this.background.displayHeight > 0) {
      this.repositionElementsWithinBounds(this.background.displayHeight);
    }
    // Otherwise, do nothing - Resize() will handle positioning later
  }

  private repositionElementsWithinBounds(containerHeight: number): void {
    const padding = Global.minFontSize * 0.5;
    const textSpacing = Global.minFontSize * 0.4;

    // Calculate available space
    const availableHeight = containerHeight - padding * 2;

    // Size icon to fit height, but not too big
    const maxIconSize = Math.min(availableHeight, Global.minFontSize * 3.5);
    const iconSize = Math.max(Global.minFontSize * 2, maxIconSize);

    // Position icon on the left with padding
    this.levelIcon.setDisplaySize(iconSize, iconSize);
    this.levelIcon.setPosition(
      padding,
      padding + (availableHeight - iconSize) * 0.5,
    );

    // Calculate text area
    const textAreaX = padding + iconSize + textSpacing;

    // Size fonts to fit within the container
    const baseFontSize = Global.minFontSize * 1.2;

    // Position level name
    this.levelNameText.setFontSize(baseFontSize);
    this.levelNameText.setPosition(textAreaX, padding);

    // Position required score text below the level name if visible
    if (this.requiredScoreText.visible) {
      this.requiredScoreText.setFontSize(Global.minFontSize);
      this.requiredScoreText.setPosition(
        textAreaX,
        padding + this.levelNameText.height + textSpacing * 0.5,
      );
    }
  }

  public Resize(maxWidth: number, maxHeight: number): this {
    // Store dimensions for later use when content changes
    this.lastMaxWidth = maxWidth;
    this.lastMaxHeight = maxHeight;

    const padding = Global.minFontSize * 0.5;
    const textSpacing = Global.minFontSize * 0.4;

    // Calculate icon size
    const availableHeight = maxHeight - padding * 2;
    const maxIconSize = Math.min(availableHeight, Global.minFontSize * 3.5);
    const iconSize = Math.max(Global.minFontSize * 2, maxIconSize);

    // Set font sizes to calculate text widths
    this.levelNameText.setFontSize(Global.minFontSize * 1.2);
    if (this.requiredScoreText.visible) {
      this.requiredScoreText.setFontSize(Global.minFontSize);
    }

    // Calculate actual content width needed
    const textAreaWidth = Math.max(
      this.levelNameText.width,
      this.requiredScoreText.visible ? this.requiredScoreText.width : 0,
    );
    const contentWidth = iconSize + textSpacing + textAreaWidth + padding * 2;

    // Use calculated width but keep fixed height
    const finalWidth = Math.min(contentWidth, maxWidth); // Don't exceed maxWidth

    this.background
      .setSize(finalWidth, maxHeight)
      .setDisplaySize(finalWidth, maxHeight);
    this.background.setPosition(
      this.background.displayWidth * 0.5,
      this.background.displayHeight * 0.5,
    );

    // Now reposition elements within this sized background
    this.repositionElementsWithinBounds(maxHeight);

    return this;
  }

  public override destroy(): void {
    Client.game.events.off(GameEvents.LEVEL_UPDATED, this.onLevelUpdated, this);
    super.destroy();
  }
}
